import type {
  ClassifiedChapterBeat,
  DramaticChapterBeat,
  FourBeatChapterContract,
  FourBeatChapterValidationResult,
  FourBeatGateResult,
  FourBeatValidationIssue,
  GeneratedBeatStatus,
  GeneratedChapterBeat,
  GeneratedFourBeatChapter,
} from '../types';
import { FOUR_BEAT_BASE_MINS } from './fourBeatChapterContract.service';
import { FOUR_BEAT_AUTHORITATIVE_IDS } from './scribeThinkingPolicy.service';
import { countChapterWords } from './chapterExpansion.service';

const DECISION_MARKERS = /\b(decide|decided|choose|chose|commit|agree|refuse|must|will|strategy|resolve|resolved|sacrifice|confront|reveal|discover)\b/i;
const CONSEQUENCE_MARKERS = /\b(because|therefore|result|cost|forced|changed|revealed|lost|gained|after|surge|shuts? down|hook|danger|vibrat)\b/i;
const SUMMARY_MARKERS =
  /\b(in summary|overall|later|eventually|the chapter ends|they would|they will|meanwhile|afterward|in the end)\b/i;
const INTRO_MARKERS =
  /\b(reached the|arrived at|first time|introducing|met for the first time|new character)\b/i;

const STRUCTURAL_BLOCK_CODES = new Set<FourBeatValidationIssue['code']>([
  'MISSING_BEAT',
  'DUPLICATE_BEAT_ID',
  'UNKNOWN_BEAT_ID',
  'WRONG_BEAT_COUNT',
  'TRUNCATED',
  'MID_SENTENCE_END',
  'NEARLY_EMPTY',
]);

const REPAIR_TRIGGER_CODES = new Set<FourBeatValidationIssue['code']>([
  'UNDER_WORD_MINIMUM',
  'MISSING_DECISION',
  'MISSING_CONSEQUENCE',
]);

export interface InspectorSummaryWarning {
  beatId: string;
  warningCode: 'POSSIBLE_SUMMARY_COMPRESSION';
  wordCount: number;
  targetMinWords: number;
  beatFunction: DramaticChapterBeat['function'];
}

export function computeAcceptanceFloorWords(targetMinWords: number, sequence?: number): number {
  if (sequence && sequence >= 1 && sequence <= 4) {
    return Math.max(300, Math.floor((FOUR_BEAT_BASE_MINS[sequence - 1] ?? 550) * 0.65));
  }
  return Math.max(300, Math.floor(targetMinWords * 0.65));
}

export function detectSummaryLikeBeat(content: string): boolean {
  const trimmed = (content || '').trim();
  if (trimmed.length < 120) return true;
  if (SUMMARY_MARKERS.test(trimmed) && !DECISION_MARKERS.test(trimmed)) return true;
  const paragraphs = trimmed.split(/\n\s*\n+/).filter(Boolean);
  const sentenceCount = trimmed.split(/(?<=[.!?])\s+/).filter(Boolean).length;
  if (trimmed.length < 400 && paragraphs.length <= 1 && sentenceCount < 4) return true;
  return false;
}

/** @deprecated Pre-Inspector repair no longer uses severe summary classification. */
export function detectSevereSummaryBeat(content: string, wordCount: number, acceptanceFloorWords: number): boolean {
  if (!detectSummaryLikeBeat(content)) return false;
  return wordCount < acceptanceFloorWords || SUMMARY_MARKERS.test(content);
}

export function detectRepeatedIntroduction(
  beats: GeneratedChapterBeat[],
  povCharacter?: string,
): boolean {
  let introCount = 0;
  for (const beat of beats) {
    const text = beat.content || '';
    if (INTRO_MARKERS.test(text)) introCount += 1;
    if (povCharacter && new RegExp(`${povCharacter}.*(arrived|reached|entered)`, 'i').test(text.slice(0, 220))) {
      introCount += 1;
    }
  }
  return introCount > 1;
}

export function detectMidSentenceEnd(content: string): boolean {
  const trimmed = (content || '').trim();
  if (!trimmed) return true;
  return !/[.!?…"']$/.test(trimmed);
}

function issue(
  partial: Omit<FourBeatValidationIssue, 'severity'> & { severity?: FourBeatValidationIssue['severity'] },
): FourBeatValidationIssue {
  return {
    severity: partial.severity || 'WARNING',
    ...partial,
  };
}

export function isStructurallyCompleteBeat(
  generated: GeneratedChapterBeat,
): boolean {
  const content = generated.content || '';
  return (
    generated.wordCount >= 30 &&
    !generated.truncated &&
    !detectMidSentenceEnd(content)
  );
}

export function classifyBeatStatus(issues: FourBeatValidationIssue[]): GeneratedBeatStatus {
  if (issues.some((item) => item.severity === 'BLOCK' || STRUCTURAL_BLOCK_CODES.has(item.code))) {
    return 'BLOCKED';
  }
  if (issues.some((item) => item.severity === 'REPAIR' || REPAIR_TRIGGER_CODES.has(item.code))) {
    return 'REPAIR_REQUIRED';
  }
  return 'ACCEPTED';
}

export function validateStructuralBeatSet(
  beats: GeneratedChapterBeat[],
  contract: FourBeatChapterContract,
): FourBeatValidationIssue[] {
  const issues: FourBeatValidationIssue[] = [];
  const expectedIds = contract.beats.map((beat) => beat.beatId);

  if (beats.length !== 4) {
    issues.push(
      issue({
        beatId: 'chapter',
        sequence: 0,
        code: 'WRONG_BEAT_COUNT',
        message: `Expected exactly 4 beats; received ${beats.length}.`,
        severity: 'BLOCK',
      }),
    );
  }

  const returnedIds = beats.map((beat) => beat.beatId);
  const idSet = new Set(returnedIds);
  if (idSet.size !== returnedIds.length) {
    for (const beatId of returnedIds) {
      if (returnedIds.filter((id) => id === beatId).length > 1) {
        issues.push(
          issue({
            beatId,
            sequence: beats.find((beat) => beat.beatId === beatId)?.sequence || 0,
            code: 'DUPLICATE_BEAT_ID',
            message: `Duplicate beat ID ${beatId}.`,
            severity: 'BLOCK',
          }),
        );
      }
    }
  }

  for (const beatId of expectedIds) {
    if (!idSet.has(beatId)) {
      issues.push(
        issue({
          beatId,
          sequence: contract.beats.find((beat) => beat.beatId === beatId)?.sequence || 0,
          code: 'MISSING_BEAT',
          message: `Missing beat ${beatId}.`,
          severity: 'BLOCK',
        }),
      );
    }
  }

  for (const beat of beats) {
    if (!expectedIds.includes(beat.beatId)) {
      issues.push(
        issue({
          beatId: beat.beatId,
          sequence: beat.sequence,
          code: 'UNKNOWN_BEAT_ID',
          message: `Unknown beat ID ${beat.beatId}.`,
          severity: 'BLOCK',
        }),
      );
    }
  }

  return issues;
}

export function validateStructuralBeatContent(
  generated: GeneratedChapterBeat,
  contractBeat: DramaticChapterBeat,
): FourBeatValidationIssue[] {
  const issues: FourBeatValidationIssue[] = [];
  const content = generated.content || '';

  if (generated.wordCount < 30) {
    issues.push(
      issue({
        beatId: generated.beatId,
        sequence: generated.sequence,
        code: 'NEARLY_EMPTY',
        message: `Beat ${generated.sequence} is empty or nearly empty (${generated.wordCount} words).`,
        severity: 'BLOCK',
      }),
    );
  }

  if (generated.truncated) {
    issues.push(
      issue({
        beatId: generated.beatId,
        sequence: generated.sequence,
        code: 'TRUNCATED',
        message: 'Beat output appears truncated.',
        severity: 'BLOCK',
      }),
    );
  }

  if (detectMidSentenceEnd(content) && generated.wordCount > 0) {
    issues.push(
      issue({
        beatId: generated.beatId,
        sequence: generated.sequence,
        code: 'MID_SENTENCE_END',
        message: 'Beat ends mid-sentence.',
        severity: 'BLOCK',
      }),
    );
  }

  return issues;
}

export function validateBeatRepairTriggers(
  generated: GeneratedChapterBeat,
  contractBeat: DramaticChapterBeat,
): FourBeatValidationIssue[] {
  const issues: FourBeatValidationIssue[] = [];
  const content = generated.content || '';
  const floor = contractBeat.acceptanceFloorWords || computeAcceptanceFloorWords(contractBeat.targetMinWords);

  if (generated.wordCount < floor) {
    issues.push(
      issue({
        beatId: generated.beatId,
        sequence: generated.sequence,
        code: 'UNDER_WORD_MINIMUM',
        message: `Beat ${generated.sequence} has ${generated.wordCount} words; structural floor ${floor}.`,
        severity: 'REPAIR',
      }),
    );
  } else if (generated.wordCount < contractBeat.targetMinWords) {
    issues.push(
      issue({
        beatId: generated.beatId,
        sequence: generated.sequence,
        code: 'BELOW_PREFERRED_TARGET',
        message: `Beat ${generated.sequence} has ${generated.wordCount} words; preferred minimum ${contractBeat.targetMinWords}.`,
        severity: 'WARNING',
      }),
    );
  }

  if (contractBeat.function === 'DECISION_REVERSAL') {
    if (!generated.decisionPresent && !DECISION_MARKERS.test(content)) {
      issues.push(
        issue({
          beatId: generated.beatId,
          sequence: generated.sequence,
          code: 'MISSING_DECISION',
          message: 'Beat 3 requires a consequential decision, action, confrontation, discovery, or reversal.',
          severity: 'REPAIR',
        }),
      );
    }
  }

  if (contractBeat.function === 'CONSEQUENCE_HOOK') {
    if (!generated.consequencePresent && !CONSEQUENCE_MARKERS.test(content)) {
      issues.push(
        issue({
          beatId: generated.beatId,
          sequence: generated.sequence,
          code: 'MISSING_CONSEQUENCE',
          message: 'Beat 4 requires an immediate consequence, state change, revelation, or forward hook.',
          severity: 'REPAIR',
        }),
      );
    }
  }

  return issues;
}

export function validateNarrativeBeatWarnings(
  generated: GeneratedChapterBeat,
  contractBeat: DramaticChapterBeat,
): FourBeatValidationIssue[] {
  const issues: FourBeatValidationIssue[] = [];
  const content = generated.content || '';
  const floor = contractBeat.acceptanceFloorWords || computeAcceptanceFloorWords(contractBeat.targetMinWords);

  if (
    detectSummaryLikeBeat(content) &&
    generated.wordCount >= floor &&
    isStructurallyCompleteBeat(generated)
  ) {
    issues.push(
      issue({
        beatId: generated.beatId,
        sequence: generated.sequence,
        code: 'POSSIBLE_SUMMARY_COMPRESSION',
        message: 'Beat may read summary-like; Inspector should evaluate dramatization quality.',
        severity: 'WARNING',
      }),
    );
  }

  if (!generated.endingState?.trim() || generated.endingState.trim().length < 20) {
    issues.push(
      issue({
        beatId: generated.beatId,
        sequence: generated.sequence,
        code: 'NO_STATE_CHANGE',
        message: 'Ending state is missing or thin; Inspector should evaluate continuity.',
        severity: 'WARNING',
      }),
    );
  }

  return issues;
}

/** @deprecated Prefer validateStructuralBeatContent + validateBeatRepairTriggers. */
export function validateGeneratedBeat(
  generated: GeneratedChapterBeat,
  contractBeat: DramaticChapterBeat,
): FourBeatValidationIssue[] {
  return [
    ...validateStructuralBeatContent(generated, contractBeat),
    ...validateBeatRepairTriggers(generated, contractBeat),
    ...validateNarrativeBeatWarnings(generated, contractBeat),
  ];
}

export function classifySingleGeneratedBeat(
  generatedBeat: GeneratedChapterBeat,
  contractBeat: DramaticChapterBeat,
): ClassifiedChapterBeat {
  const beatIssues = [
    ...validateStructuralBeatContent(generatedBeat, contractBeat),
    ...validateBeatRepairTriggers(generatedBeat, contractBeat),
    ...validateNarrativeBeatWarnings(generatedBeat, contractBeat),
  ];

  return {
    ...generatedBeat,
    status: classifyBeatStatus(beatIssues),
    issues: beatIssues,
  };
}

export function collectPreInspectorRepairCandidates(
  gate: FourBeatGateResult,
): ClassifiedChapterBeat[] {
  return gate.beats.filter((beat) =>
    beat.issues.some((item) => item.severity === 'REPAIR'),
  );
}

export function collectPreInspectorSummaryWarnings(
  generated: GeneratedFourBeatChapter,
  contract: FourBeatChapterContract,
): InspectorSummaryWarning[] {
  const warnings: InspectorSummaryWarning[] = [];

  for (const contractBeat of contract.beats) {
    const generatedBeat = generated.beats.find((beat) => beat.beatId === contractBeat.beatId);
    if (!generatedBeat) continue;

    const beatWarnings = validateNarrativeBeatWarnings(generatedBeat, contractBeat);
    if (beatWarnings.some((item) => item.code === 'POSSIBLE_SUMMARY_COMPRESSION')) {
      warnings.push({
        beatId: generatedBeat.beatId,
        warningCode: 'POSSIBLE_SUMMARY_COMPRESSION',
        wordCount: generatedBeat.wordCount,
        targetMinWords: contractBeat.targetMinWords,
        beatFunction: contractBeat.function,
      });
    }
  }

  return warnings;
}

export function buildInspectorSummaryWarningContext(
  generated: GeneratedFourBeatChapter,
  contract: FourBeatChapterContract,
): string {
  const warnings = collectPreInspectorSummaryWarnings(generated, contract);
  if (warnings.length === 0) return '';

  return [
    'PRE-INSPECTOR SUMMARY WARNINGS (route: INSPECTOR — do not auto-reject chapter):',
    ...warnings.map(
      (warning) =>
        `- ${warning.beatId} [${warning.warningCode}] wordCount=${warning.wordCount} targetMinWords=${warning.targetMinWords} beatFunction=${warning.beatFunction}`,
    ),
    'INSPECTOR SUMMARY DETERMINATION:',
    '- Judge whether each flagged beat dramatizes its assigned atomic events with physical action and enacted conflict.',
    '- Return specific beatId values and concrete reasons only when targeted repair is required.',
    '- Do NOT reject the chapter based solely on these pre-Inspector warnings.',
  ].join('\n');
}

export function classifyChapterBeats(
  generated: GeneratedFourBeatChapter,
  contract: FourBeatChapterContract,
): ClassifiedChapterBeat[] {
  const structuralIssues = validateStructuralBeatSet(generated.beats, contract);
  const repeatedIntro = detectRepeatedIntroduction(generated.beats, contract.povCharacter);

  return contract.beats.map((contractBeat) => {
    const generatedBeat = generated.beats.find((beat) => beat.beatId === contractBeat.beatId);
    if (!generatedBeat) {
      const beatIssues = structuralIssues.filter((issue) => issue.beatId === contractBeat.beatId);
      return {
        beatId: contractBeat.beatId,
        sequence: contractBeat.sequence,
        content: '',
        endingState: '',
        wordCount: 0,
        requiredEventsSatisfied: [],
        decisionPresent: false,
        consequencePresent: false,
        truncated: false,
        status: 'BLOCKED' as GeneratedBeatStatus,
        issues: beatIssues,
      };
    }

    const beatIssues = [
      ...structuralIssues.filter(
        (issue) => issue.beatId === generatedBeat.beatId || issue.beatId === 'chapter',
      ),
      ...validateStructuralBeatContent(generatedBeat, contractBeat),
      ...validateBeatRepairTriggers(generatedBeat, contractBeat),
      ...validateNarrativeBeatWarnings(generatedBeat, contractBeat),
    ];

    if (repeatedIntro && generatedBeat.sequence > 1) {
      beatIssues.push(
        issue({
          beatId: generatedBeat.beatId,
          sequence: generatedBeat.sequence,
          code: 'REPEATED_INTRODUCTION',
          message: 'Beat repeats chapter-opening introduction.',
          severity: 'WARNING',
        }),
      );
    }

    return {
      ...generatedBeat,
      status: classifyBeatStatus(beatIssues),
      issues: beatIssues,
    };
  });
}

export function evaluateFourBeatGate(
  generated: GeneratedFourBeatChapter,
  contract: FourBeatChapterContract,
  repairCallsUsed = 0,
): FourBeatGateResult {
  const classified = classifyChapterBeats(generated, contract);
  const structuralIssues = validateStructuralBeatSet(generated.beats, contract);
  const allIssues = [
    ...structuralIssues,
    ...classified.flatMap((beat) => beat.issues),
  ];
  const warnings = allIssues.filter((item) => item.severity === 'WARNING');
  const repairIssues = allIssues.filter((item) => item.severity === 'REPAIR');
  const belowPreferredBeatIds = [
    ...new Set(
      warnings
        .filter((item) => item.code === 'BELOW_PREFERRED_TARGET')
        .map((item) => item.beatId),
    ),
  ];

  const allFourBeatsPresent =
    generated.beats.length === 4 &&
    FOUR_BEAT_AUTHORITATIVE_IDS.every((beatId) => generated.beats.some((beat) => beat.beatId === beatId));
  const allBeatIdsUnique = new Set(generated.beats.map((beat) => beat.beatId)).size === generated.beats.length;
  const structurallyValid =
    allFourBeatsPresent &&
    allBeatIdsUnique &&
    !structuralIssues.some((item) => item.severity === 'BLOCK') &&
    !classified.some((beat) =>
      beat.issues.some((item) => item.severity === 'BLOCK' || STRUCTURAL_BLOCK_CODES.has(item.code)),
    );

  const hasRepairTriggers = classified.some((beat) => beat.status === 'REPAIR_REQUIRED');
  const allBeatsAccepted = classified.every((beat) => beat.status === 'ACCEPTED');
  const accepted = structurallyValid && allBeatsAccepted;
  const route: FourBeatGateResult['route'] = !structurallyValid
    ? 'BLOCKED'
    : hasRepairTriggers
      ? 'REPAIR'
      : 'INSPECTOR';

  const blockedReason = accepted
    ? undefined
    : classified.find((beat) => beat.status === 'BLOCKED')?.issues[0]?.message ||
      allIssues.find((item) => item.severity === 'BLOCK')?.message ||
      allIssues[0]?.message ||
      'Four-beat validation failed.';

  return {
    accepted,
    structurallyValid,
    allBeatsAccepted,
    allFourBeatsPresent,
    allBeatIdsUnique,
    beats: classified,
    issues: allIssues,
    warnings,
    repairIssues,
    belowPreferredBeatIds,
    route,
    repairCallsUsed,
    preInspectorRepairedBeatIds: [],
    blockedReason,
  };
}

export function validateFourBeatChapter(
  generated: GeneratedFourBeatChapter,
  contract: FourBeatChapterContract,
): FourBeatChapterValidationResult {
  const gate = evaluateFourBeatGate(generated, contract);
  return {
    valid: gate.accepted,
    issues: gate.issues,
    deficientBeatIds: gate.beats.filter((beat) => beat.status === 'REPAIR_REQUIRED').map((beat) => beat.beatId),
    beatWordCounts: generated.beats.map((beat) => beat.wordCount),
    totalWordCount: generated.totalWordCount,
    structurallyValid: gate.structurallyValid,
    allFourBeatsPresent: gate.allFourBeatsPresent,
    allBeatIdsUnique: gate.allBeatIdsUnique,
    beatStatuses: gate.beats,
  };
}

export function toGeneratedChapterBeat(
  payload: {
    beatId: string;
    sequence: number;
    content?: string;
    endingState?: string;
    requiredEventsSatisfied?: string[];
    decisionPresent?: boolean;
    consequencePresent?: boolean;
    finishReason?: string;
    outputTokenCount?: number;
    truncated?: boolean;
  },
): GeneratedChapterBeat {
  const content = payload.content || '';
  return {
    beatId: payload.beatId,
    sequence: payload.sequence,
    content,
    endingState: payload.endingState || '',
    wordCount: countChapterWords(content),
    requiredEventsSatisfied: payload.requiredEventsSatisfied || [],
    decisionPresent: Boolean(payload.decisionPresent),
    consequencePresent: Boolean(payload.consequencePresent),
    finishReason: payload.finishReason,
    outputTokenCount: payload.outputTokenCount,
    truncated: Boolean(payload.truncated),
  };
}
